import type { ShiftSegment, DayType } from '../award-engines/shift-classifier';
import type { ShiftPayWithOvertimeInput } from './daily-overtime-input';
import { ordinaryHoursForShiftWithinConfiguredLimits } from '../schads';
import { payrollBreakMinutesForEngine } from './break-treatment';

/** Clauses 26/32 and undertakings 9/10. This allocation is only used by the
 * compiled Medea General Staff contract, never by the base award or Nurses. */
export interface GeneralStaffAllocation {
  start: string;
  end: string;
  day: DayType;
  hours: number;
  allocation: 'ORDINARY' | 'OVERTIME';
}

export function generalStaffDailyWorkLimit(input: ShiftPayWithOvertimeInput): number {
  const signedTen =
    input.generalStaffDailyLimitBasis === 'SIGNED_SCHADS_10' ||
    (input.generalStaffDailyLimitBasis === 'SIGNED_AGED_CARE_NIGHT_10' &&
      input.employeeCategory !== 'dayworker');
  return Math.min(input.ordinaryHoursLimitPerShift ?? (signedTen ? 10 : 8), signedTen ? 10 : 8);
}

/** cl 26(b), 32(a)(iii), undertaking 9: a dayworker's work outside 6am-6pm
 * Monday to Friday is overtime. Casual weekend hours are the exception: cl 42(b)
 * ("A casual employee who works on a weekend will be paid ... 175% / 225% of the
 * ordinary hourly rate") governs every casual's weekend hours up to the daily
 * trigger, and a casual working weekend ordinary hours is not a day worker that
 * week (cl 6). EA reading 5 Nov 2026, medea-ga-ea-reading-d1-d4-20261105.md D2. */
export function generalStaffOutsideDayworkerSpan(
  input: Pick<ShiftPayWithOvertimeInput, 'employeeCategory' | 'employmentType'>,
  weekday: string | undefined,
  localSeconds: number,
): boolean {
  if (input.employeeCategory !== 'dayworker') return false;
  if (weekday === 'Sat' || weekday === 'Sun') return input.employmentType !== 'casual';
  return localSeconds < 6 * 3600 || localSeconds >= 18 * 3600;
}

/** A recorded interval is used exactly. If only duration is available,
 * compare candidate positions using the same full GS engine gross calculation,
 * with an earlier start as deterministic tie-break.
 * This is an assumption for review, not a claim of recorded placement. */
export function generalStaffWorkedSegments(
  input: ShiftPayWithOvertimeInput,
  segments: readonly ShiftSegment[],
  priceCandidate: (breakStart: Date, breakEnd: Date) => number,
): { segments: ShiftSegment[]; assumedPlacementReview: boolean } {
  const minutes = payrollBreakMinutesForEngine(
    input.breakMinutes ?? 0,
    input.breakTreatment,
    input.breakSemantics,
  );
  const start = input.startTime.getTime(),
    end = input.endTime.getTime(),
    duration = minutes * 60_000;
  if (
    !Number.isFinite(start) ||
    !Number.isFinite(end) ||
    end <= start ||
    !Number.isFinite(duration) ||
    duration < 0 ||
    duration > end - start
  )
    throw new Error('pricing_gs_break_geometry_invalid');
  if (duration === 0) return { segments: [...segments], assumedPlacementReview: false };
  const recordedStart = input.actualBreakStartTime?.getTime();
  const recordedEnd = input.actualBreakEndTime?.getTime();
  const supplied = input.actualBreakStartTime != null || input.actualBreakEndTime != null;
  if (
    supplied &&
    (!Number.isFinite(recordedStart) ||
      !Number.isFinite(recordedEnd) ||
      recordedStart! < start ||
      recordedEnd! > end ||
      recordedEnd! - recordedStart! !== duration)
  )
    throw new Error('pricing_gs_break_geometry_invalid');
  const clip = (breakStart: number) => {
    const breakEnd = breakStart + duration;
    return segments.flatMap((cell) => {
      const a = cell.startTime.getTime(),
        b = cell.endTime.getTime();
      return [
        [a, Math.min(b, breakStart)],
        [Math.max(a, breakEnd), b],
      ].flatMap(([from, to]) =>
        from! < to!
          ? [
              {
                ...cell,
                startTime: new Date(from!),
                endTime: new Date(to!),
                hours: (to! - from!) / 3_600_000,
              },
            ]
          : [],
      );
    });
  };
  if (supplied) return { segments: clip(recordedStart!), assumedPlacementReview: false };
  const clock = new Intl.DateTimeFormat('en-AU', {
    timeZone: input.timezone ?? 'Australia/Sydney',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const classes = new Set<string>(),
    boundaries = new Set<number>([start, end]);
  for (const cell of segments) {
    const a = cell.startTime.getTime(),
      b = cell.endTime.getTime();
    const parts = Object.fromEntries(
      clock.formatToParts(cell.startTime).map((p) => [p.type, p.value]),
    );
    const seconds = Number(parts.hour) * 3600 + Number(parts.minute) * 60 + Number(parts.second);
    const cuts = [
      a,
      ...[6 * 3600, 18 * 3600].map((n) => a + (n - seconds) * 1000).filter((n) => n > a && n < b),
      b,
    ];
    for (const cut of cuts) boundaries.add(cut);
    for (let i = 0; i < cuts.length - 1; i++) {
      const local = seconds + (cuts[i]! - a) / 1000;
      const outside = generalStaffOutsideDayworkerSpan(input, parts.weekday, local);
      classes.add(`${cell.dayType}|${outside ? 'outside' : 'inside'}`);
    }
  }
  if (classes.size <= 1) return { segments: clip(end - duration), assumedPlacementReview: false };
  const candidates = new Set<number>([start, end - duration]);
  for (const boundary of boundaries)
    for (const at of [boundary, boundary - duration])
      if (at >= start && at <= end - duration) candidates.add(at);
  // Also examine daily/weekly/ordinary cap boundaries that can lie within
  // a classifier cell. All candidates are scored with actual selected rates,
  // loading, tier carry, allowances and the engine's own rounding.
  const fragment =
    (input.ordinaryHoursWorkedEarlierThisShift ?? 0) +
    (input.overtimeHoursWorkedEarlierThisShift ?? 0);
  const weeklyLimit = Math.min(
    input.ordinaryHoursLimitPerWeek ?? Infinity,
    input.employmentType === 'casual' ? 38 : (input.generalStaffWeeklyWorkLimit ?? 38),
  );
  const dailyHeadroom =
    generalStaffDailyWorkLimit(input) - (input.generalStaffHoursWorkedEarlierToday ?? 0) - fragment;
  const weeklyHeadroom =
    weeklyLimit -
    (input.generalStaffHoursWorkedEarlierThisWeek ??
      input.ordinaryHoursWorkedEarlierThisWeek ??
      0) -
    fragment;
  const ordinaryHeadroom = ordinaryHoursForShiftWithinConfiguredLimits(
    {
      ...input,
      ordinaryHoursLimitPerShift: Number.MAX_SAFE_INTEGER,
      disableWeeklyOrdinaryHoursLimit: true,
    },
    (end - start - duration) / 3_600_000,
    Number.MAX_SAFE_INTEGER,
  );
  for (const hours of [dailyHeadroom, weeklyHeadroom, ordinaryHeadroom]) {
    if (!Number.isFinite(hours) || hours < 0) continue;
    for (const at of [
      start + hours * 3_600_000,
      start + hours * 3_600_000 + duration,
      start + hours * 3_600_000 - duration,
    ])
      if (at >= start && at <= end - duration) candidates.add(at);
  }
  let selected = start,
    best = -Infinity;
  for (const at of [...candidates].sort((a, b) => a - b)) {
    const gross = priceCandidate(new Date(at), new Date(at + duration));
    if (!Number.isFinite(gross)) throw new Error('pricing_gs_break_candidate_unavailable');
    if (gross > best) {
      selected = at;
      best = gross;
    }
  }
  return { segments: clip(selected), assumedPlacementReview: true };
}

/** Allocation uses worked-hour triggers and ordinary-hour caps independently.
 * An out-of-span hour consumes worked counters but is never counted twice as
 * overtime when it also crosses a daily, weekly or ordinary-hours ceiling. */
export function allocateGeneralStaffOvertime(
  input: ShiftPayWithOvertimeInput,
  segments: readonly ShiftSegment[],
): GeneralStaffAllocation[] {
  for (const n of [
    input.generalStaffHoursWorkedEarlierToday,
    input.generalStaffHoursWorkedEarlierThisWeek,
    input.ordinaryHoursWorkedEarlierThisWeek,
    input.ordinaryHoursWorkedEarlierThisShift,
    input.overtimeHoursWorkedEarlierThisShift,
    input.ordinaryHoursLimitPerShift,
    input.ordinaryHoursLimitPerWeek,
  ]) {
    if (n !== undefined && (!Number.isFinite(n) || n < 0))
      throw new Error('general_staff_overtime_counter_invalid');
  }
  const total = segments.reduce((n, s) => n + s.hours, 0);
  const earlierFragment =
    Math.max(0, input.ordinaryHoursWorkedEarlierThisShift ?? 0) +
    Math.max(0, input.overtimeHoursWorkedEarlierThisShift ?? 0);
  const dailyBefore = Math.max(0, input.generalStaffHoursWorkedEarlierToday ?? 0) + earlierFragment;
  const weeklyBefore =
    Math.max(
      0,
      input.generalStaffHoursWorkedEarlierThisWeek ?? input.ordinaryHoursWorkedEarlierThisWeek ?? 0,
    ) + earlierFragment;
  const weeklyLimit = Math.min(
    input.ordinaryHoursLimitPerWeek ?? Infinity,
    input.employmentType === 'casual' ? 38 : (input.generalStaffWeeklyWorkLimit ?? 38),
  );
  const workedHeadroom = Math.max(
    0,
    Math.min(generalStaffDailyWorkLimit(input) - dailyBefore, weeklyLimit - weeklyBefore),
  );
  // The configured 76-hour (and any stricter approved) window still applies.
  // Its accumulator is ORDINARY hours, not the worked-hour counter above.
  let ordinaryRemaining = ordinaryHoursForShiftWithinConfiguredLimits(
    {
      ...input,
      ordinaryHoursLimitPerShift: Number.MAX_SAFE_INTEGER,
      disableWeeklyOrdinaryHoursLimit: true,
    },
    total,
    Number.MAX_SAFE_INTEGER,
  );
  const clock = new Intl.DateTimeFormat('en-AU', {
    timeZone: input.timezone ?? 'Australia/Sydney',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const result: GeneralStaffAllocation[] = [];
  let worked = 0;
  const emit = (
    start: number,
    end: number,
    hours: number,
    day: DayType,
    allocation: GeneralStaffAllocation['allocation'],
  ) => {
    if (hours > 1e-10)
      result.push({
        start: new Date(start).toISOString(),
        end: new Date(end).toISOString(),
        hours,
        day,
        allocation,
      });
  };
  for (const segment of segments) {
    const start = segment.startTime.getTime(),
      end = segment.endTime.getTime();
    if (
      !Number.isFinite(start) ||
      !Number.isFinite(end) ||
      end <= start ||
      !Number.isFinite(segment.hours) ||
      segment.hours < 0
    )
      throw new Error('general_staff_overtime_segment_invalid');
    if (segment.hours === 0) continue;
    const parts = Object.fromEntries(
      clock.formatToParts(segment.startTime).map((p) => [p.type, p.value]),
    );
    const seconds = Number(parts.hour) * 3600 + Number(parts.minute) * 60 + Number(parts.second);
    // A classifier cell may straddle 06:00/18:00 when commencement is not on
    // a quarter-hour boundary. Split exactly, preserving proportional break hours.
    const cuts = [
      start,
      ...[6 * 3600, 18 * 3600]
        .map((s) => start + (s - seconds) * 1000)
        .filter((t) => t > start && t < end),
      end,
    ].sort((a, b) => a - b);
    for (let i = 0; i < cuts.length - 1; i++) {
      const a = cuts[i]!,
        b = cuts[i + 1]!;
      const hours = (segment.hours * (b - a)) / (end - start);
      const localSeconds = seconds + (a - start) / 1000;
      const outside = generalStaffOutsideDayworkerSpan(input, parts.weekday, localSeconds);
      const ordinary = outside
        ? 0
        : Math.max(0, Math.min(hours, workedHeadroom - worked, ordinaryRemaining));
      const split = a + (b - a) * (ordinary / hours);
      emit(a, split, ordinary, segment.dayType, 'ORDINARY');
      emit(split, b, hours - ordinary, segment.dayType, 'OVERTIME');
      ordinaryRemaining -= ordinary;
      worked += hours;
    }
  }
  return result;
}
